<template>
  <div class="product-spec-container">
    <div class="product-spec">
      <div class="title" v-if="productSpecList.length > 0">
        Select your desired item color(s)
      </div>
      <div class="spec-sample-tips" v-if="isSingleSizeSelection">
        <div style="margin: 10px">
          Only one capacity can be selected for this product.
        </div>
      </div>
      <div :class="[
        'product-spec__form',
        'error-block',
        specErrorMsg ? 'is-error' : ''
      ]">
        <table class="product-spec__groups">
          <tbody :class="[
            'product-spec-group',
            group.displayType === 'dropdown' ? 'is-row' : ''
          ]" v-for="(group, groupIndex) in productSpecList" :key="group.name">
            <tr v-if="isMultiGroup && group.displayType !== 'dropdown'">
              <th colspan="2">{{ group.name }}</th>
            </tr>
            <tr v-if="group.displayType == 'image' || group.displayType === 'button'
            " @mouseleave="handleMouseLeave">
              <td colspan="2">
                <ProductSpecGroupItem v-for="(color, index) in getGroupValues(group)" :color="color"
                  :displayType="group.displayType" :group-index="groupIndex" :key="index" :taken="color.selected"
                  :selected="isSelected(group, color)" :class="group.values.length === 1 ? 'is-one' : ''"
                  @click="onColorClick(groupIndex, group, color)" @hover="onColorMouseEnter">
                </ProductSpecGroupItem>
                <ap-button type="default" class="view-more-colors" v-if="isViewMoreVisible &&
                  group.displayType === 'button' &&
                  group.values.length > 10
                " text @click="isViewMoreVisible = false">More colors...</ap-button>
              </td>
            </tr>
            <tr class="product-spec-group" v-else-if="group.values.length === 1">
              <th>{{ group.name }}</th>
              <td>
                <ProductSpecGroupItem v-for="(color, index) in group.values" :color="color"
                  :displayType="group.displayType" :group-index="groupIndex" :key="index" :taken="color.selected"
                  :selected="isSelected(group, color)" class="is-one">
                </ProductSpecGroupItem>
              </td>
            </tr>
            <tr v-else class="product-spec-group__select">
              <th>{{ group.name }}</th>
              <td>
                <productSpecSelect v-model="selectedValues[groupIndex].value" :values="group.values.filter(
                  (f) => maxAssortNum > 1 || f.value !== '-1'
                )" @change="(val: ProductSpecItem) => onSelect(group, val)"></productSpecSelect>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="product-spec__inner-btn" v-if="isMultiGroup && maxAssortNum > 1">
          <ap-button type="primary" size="small" @click="onColorAdd">Add</ap-button>
        </div>
        <div class="error-label mt5 ml0" v-html="specErrorMsg"></div>
      </div>
    </div>
    <div :class="[
      'error-block',
      maxAssprtedColorError || qtyErrorMsg ? 'is-error' : ''
    ]">
      <div class="product-spec__wrapper">
        <div :class="[
          'product-spec__list',
          'table',
          appState.getOrderType === OrderType.Normal ? 'border' : ''
        ]" v-show="selectedList.length > 0">
          <table class="sample-table" v-if="appState.getOrderType === OrderType.SpecSample">
            <tr>
              <th>Quantity</th>
              <td>1</td>
            </tr>
            <tr v-if="Array.isArray(selectedList[0].quantity)">
              <th>Size</th>
              <td>
                <select v-model="selectedList[0].specSampleSizeType">
                  <option v-for="item in selectedList[0].quantity" :key="item.size" :value="item.size">{{ item.size }}
                  </option>
                </select>
              </td>
            </tr>
          </table>
          <template v-else>
            <product-spec-list v-for="(item, i) in selectedList" :data="item" :key="item.objectId" :index="i"
              :maxAssortNum="maxAssortNum" :isMultiSize="isMultiSize" :specList="productSpecList"
              :showStock="isShowStock" @remove="onItemRemove" @qtyChanged="onUpdateQty" @update:specSampleSizeType="onUpdateSpecSampleSizeType
              "></product-spec-list>
          </template>
        </div>

        <div class="no-border" v-show="showAddMore">
          <span>+ click colors to add more</span>
        </div>
      </div>

      <div class="error-label" v-show="manySizeError">Only one capacity can be selected for this product.
      </div>
      <div class="error-label" v-show="qtyErrorMsg" v-text="qtyErrorMsg"></div>
      <div class="error-label" v-show="maxAssprtedColorError" v-text="maxAssprtedColorError"></div>
    </div>
    <div class="product-spec-btns" v-show="selectedList.length > 0">
      <ap-button type="primary" size="small" @click="onContinueClick" v-show="!submitSatus"
        :disabled="qtyErrorMsg !== ''">Continue</ap-button>

      <img src="https://img66.anypromo.com/frontimg/2023/Icon/loader.gif" alt="" class="submit-loading"
        v-show="submitSatus">

      <div class="min-quantity" v-if="minQty - totalQty > 0 && selectedList.length > 0">
        {{ totalQty }} pieces. (Add {{ minQty - totalQty }} more to meet the
        minimum)
      </div>
      <div class="min-quantity" v-else-if="maxQty > 0 && maxQty < totalQty">
        Decrease {{ Math.abs(maxQty - totalQty) }} more to meet the maximum.
      </div>
      <div class="min-quantity" v-else-if="isShowTotal">
        Total
        {{ totalQty > 1 ? totalQty + ' pieces' : '1 piece' }}.
      </div>
    </div>
  </div>
</template>
<script lang="ts"></script>

<script setup lang="ts">
import { computed, inject, onMounted, reactive, ref } from 'vue'
import ProductSpecList from './productSpecList.vue'
import ProductSpecGroupItem from './productSpecGroupItem.vue'
import productSpecSelect from './productSpecSelect.vue'
import {
  OrderType,
  type ProductSpec,
  type ProductSpecItem,
  type SelectedProductSpec,
  type SelectedProductSpecItem
} from '../../types'
import { buildSpecImg, ProductSpecModel } from '../../model'
import { SHOW_BIG_IMAGE_KEY, SHOW_PRICE_GROUP_KEY } from '../../token'
import {
  checkQty,
  getMaxQty,
  getMinQty,
  getTotalQty,
  useWindow
} from '../../composables'
import '../../style/product-spec-form.less'
import { ApButton } from '@anypromo/components-pc'
import { useApp } from '../../store/modules/app'
import { useProductSpec } from '../../store/modules/productSpec'
import { useImprintMethod } from '../../store/modules/imprintMethod'
import '../../style/product-spec.less'
import { storeToRefs } from 'pinia'
import { useOrder } from '../../composables/use-order'

interface Props {
  submitSatus: boolean,
  productSpecList: ProductSpec[]
}

const props = withDefaults(
  defineProps<Props>(),
  {
    submitSatus: false,
    productSpecList: () => []
  }
)


const emit = defineEmits({
  'go-next': () => true,
  'item-remove': () => true,
  updateModel: (val: ProductSpecModel) => true,
  qtyChanaged: (val: ProductSpecModel) => true
})
const getGroupValues = (spec: ProductSpec) => {
  if (spec.displayType !== 'button' || isViewMoreVisible.value === false)
    return spec.values

  return spec.values.filter((item, index) => index < 10)
}

const productSpecListState = useProductSpec()

const { isSingleSizeSelection } = useOrder();

// 是否有错误
const specErrorMsg = ref('')
const maxAssprtedColorError = ref('')
const qtyErrorMsg = ref('')
const manySizeError = ref(false)
const appState = useApp()
const imprintMethodState = useImprintMethod()
const { productSpecList } = reactive(props)

const isMultiGroup = productSpecList.length > 1
const isViewMoreVisible = ref(false)

const selectedList = computed(() => productSpecListState.getSelectedList)

const { minQty, maxQty, maxAssortNum } = storeToRefs(appState);
/**
 * 临时选中的值
 */
const selectedValues = ref<SelectedProductSpecItem[]>(
  productSpecList.map((f) => {
    if (maxAssortNum.value === 1 && selectedList.value.length === 1) {
      const spec = selectedList.value[0].productSpec.find(
        (sp) => sp.name === f.name
      )

      if (spec) {
        return {
          name: f.name,
          value: spec.value
        }
      }
    }
    return {
      name: f.name,
      value: f.values.length === 1 ? f.values[0] : undefined
    }
  })
)

const showPriceGroup = inject(SHOW_PRICE_GROUP_KEY)
if (!showPriceGroup) {
  console.warn('build error')
}

const showBigImage = inject(SHOW_BIG_IMAGE_KEY)
if (!showBigImage) {
  console.warn('build error')
}

const onSelect = (group: ProductSpec, val: ProductSpecItem) => {

  if (beforeCheckColor(group.name, val) === false) {
    return
  }

  // 如果不支持assorted，更新store里第一个对象的值
  if (maxAssortNum.value === 1) {
    if (selectedList.value.length > 0) {
      selectedList.value[0].setValue(group.name, val)
    }
  }
}


const totalQty = computed(() => getTotalQty(selectedList.value))

const isShowTotal = computed(() => {
  if (selectedList.value.length === 0) return false

  if (Array.isArray(selectedList.value[0].quantity)) {
    return (
      selectedList.value[0].quantity.filter((it) => {
        return it.quantity !== ''
      }).length > 1
    )
  } else {
    return selectedList.value.length > 1
  }
})

const showAddMore = computed(
  () => {
    return selectedList.value.length < maxAssortNum.value &&
      appState.getOrderType !== OrderType.SpecSample
  }
)

const isShowStock = computed(() => {
  return selectedList.value.some((f) => {
    if (Array.isArray(f.quantity)) {
      return f.quantity.some((q) => q.stock && q.stock.status)
    } else {
      return f.quantity.stock && f.quantity.stock.status
    }
  })
})

const isSelected = (
  group: ProductSpec,
  color: ProductSpecItem
) => {
  // 如果支持assorted
  if (isMultiGroup) {
    // 先判断是否支持assorted
    if (maxAssortNum.value === 1) {
      // 只有一组颜色
      return selectedList.value.some(
        (f) =>
          f.productSpec.find((sp) => sp.name === group.name)?.value.value ===
          color.value
      )
    } else {
      // 从selected Values里
      return (
        selectedValues.value.find((f) => f.name === group.name)?.value
          ?.value === color.value
      )
    }
  } else if (productSpecList.length === 1) {
    // 只有一组颜色
    return selectedList.value.some(
      (f) => f.productSpec[0].value.value === color.value
    )
  }
  return false
}

const validateMultiSize = () => {
  // 是否只能选择一个尺寸
  if (!isSingleSizeSelection) return true;

  manySizeError.value = false;
  // 如果是sunjoy且支持多size，那就检查size个数
  if (isMultiSize) {
    if (Array.isArray(selectedList.value[0].quantity)) {
      const hasManySize = selectedList.value[0].quantity.filter(it => it.quantity !== '').length > 1;
      if (hasManySize) {
        manySizeError.value = true;
        return false;
      }
    }

  }
  return true

}
const onContinueClick = () => {

  manySizeError.value = false;
  // 检查qty
  const qtyValid = validateQty();
  const sizeValid = validateMultiSize();

  if (!qtyValid || !sizeValid) {
    return;
  }

  if (appState.getOrderType === OrderType.SpecSample) {
    if (Array.isArray(selectedList.value[0].quantity)) {
      const qty = selectedList.value[0].getQty();
      selectedList.value[0].quantity.forEach(it => {
        if (it.size == selectedList.value[0].specSampleSizeType) {
          it.quantity = qty;
        } else {
          it.quantity = ''
        }
      })
    }
  }
  emit('go-next')
}

const { assortedMinQtyPerColor, assortedMultipleSoldQtyPerColor, isMultiSize } =
  useWindow()

const onAddProduct = (values: SelectedProductSpec[]) => {
  const model = new ProductSpecModel()
  model.setProductSpec(values)
  let qty = getMinQty(appState.getCurrentPriceList)
  if (selectedList.value.length === 0) {
    const minQty = getMinQty(
      appState.getCurrentPriceList,
      imprintMethodState.getSelectedList,
      true
    )
    // 如果没有，那就用传递过来的值
    qty = Math.max(qty, minQty)
  } else if (isMultiSize === false) {
    if (assortedMinQtyPerColor > 0) {
      qty = assortedMinQtyPerColor
    } // 倍数
    if (assortedMultipleSoldQtyPerColor > 1) {
      qty =
        Math.ceil(qty / assortedMultipleSoldQtyPerColor) *
        assortedMultipleSoldQtyPerColor
    }
  }

  model.setQty(qty)
  productSpecListState.add(model)

  // 调用事件
  emit('updateModel', model)

}

const handleMouseLeave = (evt: MouseEvent) => {
  // window.$('#zoomBigImage').remove()

  if (showBigImage) {
    showBigImage.hide()
  }
}
const onColorMouseEnter = (
  groupdIndex: number,
  color: ProductSpecItem,
  target: HTMLElement
) => {
  // 先获取选中次数

  var str = ''
  if (productSpecList.length <= 1) {
    str = `<label>${productSpecList[0].name}:&nbsp;</label>${color.text}`
    if (color.outOfStock) {
      str +=
        '<div class="out-of-stock"><span class="tips">Out of Stock</span></div>'
    }
  } else {
    str = selectedValues.value
      .map(function (item, idx) {
        var spec = {
          name: item.name,
          selectedText: item.value?.text
        }
        if (groupdIndex === idx) {
          spec.selectedText = color.text
        }
        return (
          '<div><label>' +
          spec.name +
          ':&nbsp;</label>' +
          spec.selectedText +
          '</div>'
        )
      })
      .join('')
    if (color.outOfStock) {
      str +=
        '<div class="out-of-stock"><span class="tips">Out of Stock</span></div>'
    }
  }
  const imgUrl = buildSpecImg({
    list: selectedValues.value.map((f) => f.value),
    index: groupdIndex,
    color,
    size: 'big'
  })
  if (imgUrl !== undefined) {
    if (showBigImage) {
      showBigImage.show(imgUrl, str, target)
    }
  }
}
const onColorAdd = () => {
  /**
   * 如果只有一个维度，那就直接返回。正常一个维度是不会显示add按钮
   */
  if (productSpecList.length < 2) {
    return
  }
  // 如果超过assorted限制，那就给出提示
  if (selectedList.value.length >= maxAssortNum.value) {
    showExceedAssortedNum()
    return
  }

  specErrorMsg.value = ''
  const errorList = Array<string>()
  selectedValues.value.forEach((f) => {
    if (!f.value || f.value.value === '-1') {
      errorList.push(f.name)
    }
  })
  if (errorList.length > 0) {
    specErrorMsg.value = 'Please, select ' + errorList.join(' and ') + '.'
    return
  }
  /*
  判断是否有重复
   */
  if (productSpecListState.exists(selectedValues.value)) {
    // 如果存在重复，就给出提示
    specErrorMsg.value = 'Duplicate color already exists.'
    return
  }

  onAddProduct(
    selectedValues.value.map((f) => {
      return {
        name: f.name,
        value: f.value!
      }
    })
  )
  if (productSpecList.length > 1) {
    productSpecList.forEach((sp) => {
      sp.values.forEach((val) => {
        val.selected = false
      })
    })
  }
  selectedValues.value.forEach((f) => {
    f.value = props.productSpecList.find(sp => f.name == sp.name)?.values.find(val => val.value === '-1')
  })
}
const onColorClick = (
  groupIndex: number,
  group: ProductSpec,
  color: ProductSpecItem
) => {
  if (group.displayType === 'image' || group.displayType === 'button') {
    if (isSelected(group, color)) {
      // 如果支持多group，或者支持assorted，那就允许取消
      // 如果不支持assorted，那就不允许取消
      if (maxAssortNum.value === 1) {
        // 不允许取消
      } else {
        if (isMultiGroup) {
          selectedValues.value[groupIndex].value = undefined
        } else if (productSpecList.length === 1) {
          // 自动删除
          const index = productSpecListState.getSelectedList.findIndex(
            (f) => f.productSpec[0].value.value === color.value
          )
          if (index !== -1) {
            productSpecListState.removeAt(index)
          }
        }
      }
      return
    }
  }

  if (beforeCheckColor(group.name, color) === false) {
    return
  }
  if (maxAssortNum.value === 1) {
    if (productSpecListState.getSelectedList.length > 0) {

      // 需要更新图片了
      productSpecListState.getSelectedList[0].setValue(group.name, color)

      // 支持assorted ,那就修改临时变量里的值
      selectedValues.value[groupIndex].value = color

      // 更新库存
      emit('updateModel', productSpecListState.getSelectedList[0])

      imprintMethodState.checkSameColor();
    }
  } else {
    if (isMultiGroup) {
      // 修改值
      // 支持assorted ,那就修改临时变量里的值
      selectedValues.value[groupIndex].value = color

      // 计算disabled
      const unSelectedItems = selectedValues.value.filter(
        (f) => f.value === undefined
      )
      if (unSelectedItems.length <= 1) {
        // 已选择的数据，按照,连接起来。
        const tmpSelectedList = selectedList.value.map((item) => {
          return item.productSpec
            .map(function (val) {
              return val.value.value
            })
            .join(',')
        })
        // 临时显示选中项
        const tmpSelectedItems = selectedValues.value.map(
          (f) => f.value?.value ?? ''
        )

        if (unSelectedItems.length === 1) {
          // 如果还差一个没选中，那就就算不可选逻辑
          const emptyIndex = productSpecList.findIndex(
            (f) => f.name === unSelectedItems[0].name
          )
          const spec = productSpecList[emptyIndex]

          productSpecList[emptyIndex].values.forEach((val) => {
            if (val.enabled === false) return
            val.selected = false
            tmpSelectedItems[emptyIndex] = val.value
            const tmp = tmpSelectedItems.join(',')
            if (tmpSelectedList.includes(tmp)) {
              val.selected = true
            }
          })
          productSpecList
            .filter((it, i) => i !== emptyIndex)
            .forEach((sp) => {
              sp.values.forEach((val) => {
                if (val.enabled === false) return
                val.selected = false
              })
            })
        } else {
          productSpecList.forEach((spec, index) => {
            spec.values.forEach(function (val) {
              if (val.enabled === false) return
              val.selected = false

              const tmpArr = [...tmpSelectedItems]
              tmpArr[index] = val.value
              const tmp = tmpArr.join(',')
              if (tmpSelectedList.includes(tmp)) {
                val.selected = true
              }
            })
          })
        }
      }
    } else if (productSpecList.length === 1) {
      // 直接添加
      onAddProduct([
        {
          name: group.name,
          value: color
        }
      ])
    }
  }
}

const beforeCheckColor = (name: string, color: ProductSpecItem) => {
  if (color.enabled === false) {
    // 需要弹出提示
    //  options: ShowPopupPriceGroupProvideOption
    // ShowPopupPriceGroupProvideOption
    showPriceGroup!({
      type: 'productSpec',
      value: {
        name: name,
        value: color.value,
        text: color.text
      }
    }).then((data) => {
      console.log('showPriceGroup')
    })
    return false
  }
  if (appState.getMaxAssortNum === 1) {
    return true
  }
  if (isMultiGroup) {
    return true
  }

  if (selectedList.value.length >= appState.getMaxAssortNum) {
    showExceedAssortedNum()
    return false
  }
  return true
}
const onUpdateSpecSampleSizeType = (
  index: number,
  value: string
) => {
  selectedList.value[index].specSampleSizeType = value
}

const onUpdateQty = (index: number, size: string, value: string) => {
  const model = selectedList.value[index]
  if (Array.isArray(model.quantity)) {
    model.quantity.some((f) => {
      if (f.size === size) {
        if (value === '') {
          f.quantity = value
          f.stock = undefined
        } else {
          f.quantity = parseInt(value)
        }
        return true
      }
      return false
    })
  } else {
    const numValue = parseInt(value)
    if (isNaN(numValue)) {
      model.quantity.quantity = ''
      model.quantity.stock = undefined
    } else {
      model.quantity.quantity = numValue
    }
  }

  // 检查qty
  const qtyValid = validateQty();
  const sizeValid = validateMultiSize();

  if (!qtyValid || !sizeValid) {
    return;
  }
  emit('updateModel', model)
}

const onUpdateProduct = (values: SelectedProductSpec[]) => {
  // 找到一个商品
  if (selectedList.value.length === 0) {
    onAddProduct(values)
  } else {
    selectedList.value[0].setProductSpec(values)
  }
}

const onItemRemove = (val: ProductSpecModel) => {
  const index = selectedList.value.indexOf(val)
  if (index !== -1) {
    productSpecListState.removeAt(index)
  }
  validateQty();

  emit('item-remove')
}

const validateQty = (): boolean => {
  if (selectedList.value.length === 0) {
    qtyErrorMsg.value = ''
    return true;
  }
  // 检查qty
  const checkResult = checkQty({
    isFreeGiftCustomize: false,
    assortedMaxColor: maxAssortNum.value ?? 1,
    productSpecList: selectedList.value,
    imprintMethodList: imprintMethodState.getSelectedList,
    priceList: appState.getCurrentPriceList
  })

  if (checkResult !== undefined) {
    qtyErrorMsg.value = checkResult
    return false
  } else {
    qtyErrorMsg.value = ''
    return true;
  }

}
const showExceedAssortedNum = () => {
  maxAssprtedColorError.value =
    'Currently only ' +
    maxAssortNum.value +
    ' color' +
    (maxAssortNum.value === 1 ? '' : 's') +
    ' can be ordered.'
  setTimeout(() => {
    maxAssprtedColorError.value = ''
  }, 3000)
}

const getValue = (): SelectedProductSpecItem[] => {
  return selectedValues.value
}

const setValue = (values: SelectedProductSpecItem[]) => {
  if (values.length > 0) {
    selectedValues.value.forEach(f => {
      var sp = values.find(val => val.name === f.name)
      if (sp) {
        f.value = sp.value;
      }
    })
  }
}

defineExpose({ getValue, setValue, validateQty })

</script>

<style lang="less">
.qty-title {
  font-weight: 500;
  font-size: 13px;
  vertical-align: top;
  padding: 14px 15px 14px 0;
  width: 55px;
}

.ap-select {
  position: static;
}
</style>
